/**
 * accountConsentRestore -- REK-3389
 *
 * After a session exists, restore the user's saved cookie decision from
 * `user_consents` (`analytics_tracking`, `marketing`, `third_party_cookies`) into the local
 * CookieConsent record so GTM Consent Mode / PostHog match the account and the
 * cookie banner stays hidden for signed-in users who already decided.
 *
 * The read uses the same query as `TrackingPreferencesCard.loadPreferences`
 * (non-revoked `user_consents` rows for the user). The mapping + apply logic
 * lives in `applyAccountConsents` (src/lib/cookieConsent.ts).
 */

import { supabase } from '@/integrations/supabase/client';
import {
  applyAccountConsents,
  hasExplicitLocalConsent,
  type AccountConsentRow,
} from '@/lib/cookieConsent';

// `third_party_cookies` is the legacy TrackingPreferencesCard alias for
// `marketing`; `applyAccountConsents` prefers `marketing` and falls back to it.
const RESTORED_CONSENT_TYPES: ReadonlyArray<string> = [
  'analytics_tracking',
  'marketing',
  'third_party_cookies',
];

/**
 * Returns `true` iff the local consent record changed. Never throws: a failed
 * read leaves local consent untouched (the banner / implied path still apply).
 */
export async function restoreConsentFromAccount(userId: string): Promise<boolean> {
  if (!userId) return false;
  // An explicit local choice always wins -- skip the network round trip.
  if (hasExplicitLocalConsent()) return false;

  try {
    const { data, error } = await supabase
      .from('user_consents')
      .select('consent_type, granted')
      .eq('user_id', userId)
      .is('revoked_at', null);
    if (error || !data) return false;

    const rows: AccountConsentRow[] = data.filter((row) =>
      RESTORED_CONSENT_TYPES.includes(row.consent_type),
    );
    return applyAccountConsents(rows);
  } catch (err) {
    console.log(
      JSON.stringify({
        event: 'consent_restore_failed',
        message: err instanceof Error ? err.message : String(err),
      }),
    );
    return false;
  }
}
